import React from "react";
import { Box, Typography, ButtonBase, LinearProgress, Chip } from "@mui/material";
import { CustomInput } from "@shared/components";
import StarRoundedIcon from "@mui/icons-material/StarRounded";

const ratingColor = (val) => val <= 4 ? "#e60000" : val <= 6 ? "#FF9500" : "#34C759";
const ratingBg    = (val) => val <= 4 ? "#fff0f0" : val <= 6 ? "#FFF8F0" : "#f0fff4";
const statusLabel = (val) => val <= 4 ? "Needs Improvement" : val <= 6 ? "Satisfactory" : "Good / Excellent";

/**
 * RatingInput — mobile-first score input.
 *
 * Props:
 *   rating          {number|null}
 *   remarks         {string}
 *   onRatingChange  {fn(number)}
 *   onRemarksChange {fn(string)}
 *   disabled        {boolean}
 *   error           {string|null}
 */
const RatingInput = ({
  rating,
  remarks,
  onRatingChange,
  onRemarksChange,
  disabled = false,
  error = null,
}) => {
  const requiresRemarks = rating !== null && rating < 7;
  const color = rating ? ratingColor(rating) : null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

      {/* ── Rating grid ──────────────────────────────────────────── */}
      <Box>
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5, color: "#1a1a1a" }}>
          Select Rating{" "}
          <Typography component="span" color="error">*</Typography>
        </Typography>

        {/* 5×2 on mobile, 10×1 on sm+ */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(5, 1fr)", sm: "repeat(10, 1fr)" }, gap: 1 }}>
          {Array.from({ length: 10 }, (_, i) => i + 1).map((val) => {
            const isSelected = rating === val;
            const btnColor   = ratingColor(val);
            return (
              <ButtonBase
                key={val}
                disabled={disabled}
                onClick={() => !disabled && onRatingChange(val)}
                sx={{
                  borderRadius: "50%",
                  width: "100%",
                  aspectRatio: "1",
                  minWidth: 0,
                  fontWeight: 700,
                  fontSize: "0.75rem",
                  border: "2px solid",
                  borderColor: isSelected ? btnColor : "#e0e0e0",
                  backgroundColor: isSelected ? btnColor : "#fafafa",
                  color: isSelected ? "#fff" : "#555",
                  transition: "all 0.15s ease",
                  "&:hover:not([disabled])": {
                    borderColor: btnColor,
                    backgroundColor: `${btnColor}18`,
                    color: btnColor,
                  },
                  "&[disabled]": { opacity: 0.45 },
                }}
              >
                {val}
              </ButtonBase>
            );
          })}
        </Box>
      </Box>

      {/* ── Score preview card ───────────────────────────────────── */}
      {rating !== null && (
        <Box
          sx={{
            borderRadius: 2, px: 1.5, py: 1,
            backgroundColor: ratingBg(rating),
            border: `1.5px solid ${color}30`,
            display: "flex", alignItems: "center", gap: 1.25,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.4, flexShrink: 0 }}>
            <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, color, lineHeight: 1 }}>{rating}</Typography>
            <Typography sx={{ fontSize: "0.7rem", color: "#bbb" }}>/10</Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={rating * 10}
            sx={{
              flex: 1, height: 7, borderRadius: 4,
              backgroundColor: `${color}20`,
              "& .MuiLinearProgress-bar": { backgroundColor: color, borderRadius: 4 },
            }}
          />
          <Chip
            icon={<StarRoundedIcon sx={{ fontSize: "12px !important", color: `${color} !important` }} />}
            label={statusLabel(rating)}
            size="small"
            sx={{
              backgroundColor: "#fff", border: `1px solid ${color}40`,
              color, fontWeight: 700, fontSize: "0.68rem", borderRadius: 2, flexShrink: 0,
            }}
          />
        </Box>
      )}

      {/* ── Remarks (CustomInput) ────────────────────────────────── */}
      {rating !== null && (
        <CustomInput
          label={requiresRemarks ? "Remarks (required for rating < 7)" : "Remarks (optional)"}
          required={requiresRemarks}
          multiline
          rows={2}
          value={remarks || ""}
          onChange={(e) => onRemarksChange(e.target.value)}
          disabled={disabled}
          placeholder="Enter your remarks..."
        />
      )}

      {/* ── Validation error ─────────────────────────────────────── */}
      {error && (
        <Typography variant="caption" color="error" sx={{ display: "block", mt: -1.5 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default RatingInput;
